import { useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

/** Medical consultation API (Phase 2) — see MEDICAL_CONSULTATION_MODULE.md. */

export interface PrescriptionItem {
  id?: string;
  medicine_name: string;
  dosage?: string | null;
  frequency?: string | null;          // free text e.g. "Twice daily after food"
  times_per_day?: number | null;      // structured (for reminders)
  duration_days?: number | null;
  notes?: string | null;
}

export interface MedicalTest {
  id?: string;
  test_name: string;
  notes?: string | null;
}

export interface Consultation {
  id: string;
  token_id: string;
  follow_up_date: string | null;
  notes: string | null;
  customer_name: string | null;
  customer_country_code: string | null;
  customer_phone: string | null;
  created_at: string;
}

export interface ConsultationBundle {
  consultation: Consultation | null;
  patient: { name: string | null; country_code: string | null; phone: string | null };
  prescriptions: PrescriptionItem[];
  tests: MedicalTest[];
}

export interface SaveConsultationBody {
  follow_up_date?: string | null;
  notes?: string | null;
  prescriptions: PrescriptionItem[];
  tests: MedicalTest[];
}

const TOKENS = API_ENDPOINTS.TOKENS;

export const useConsultation = () => {
  const getConsultation = useCallback(async (tokenId: string): Promise<ConsultationBundle> => {
    const res = await axiosInstance.get(`${TOKENS}/${tokenId}/consultation`);
    return res.data.data;
  }, []);

  const saveConsultation = useCallback(async (tokenId: string, body: SaveConsultationBody): Promise<ConsultationBundle> => {
    const res = await axiosInstance.put(`${TOKENS}/${tokenId}/consultation`, body);
    return res.data.data;
  }, []);

  const getHistory = useCallback(async (countryCode: string, phone: string): Promise<Array<Consultation & { prescriptions: PrescriptionItem[]; tests: MedicalTest[] }>> => {
    const qs = new URLSearchParams({ country_code: countryCode, phone });
    const res = await axiosInstance.get(`${API_ENDPOINTS.CONSULTATION_HISTORY}?${qs.toString()}`);
    return res.data?.data?.consultations ?? [];
  }, []);

  return { getConsultation, saveConsultation, getHistory };
};
